import { Component, ElementRef, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { DataTaskService } from '@app/core/services/data-task.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { UtilityService } from '@app/core/services/utility.service';
import { BaseWidget, NgCompInputs } from 'gridstack/dist/angular';
import { GridStack } from 'gridstack';
import { DashboardChartModalComponent } from '../dashboard-chart-modal/dashboard-chart-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { SlicerSubjectData } from '../slicers/dashboard-slicer-widget-base/dashboard-slicer-widget-base.component';
import { DashboardContext, KpiCompKpiSlicerData, KpiSlicerBaseData } from '../dashboard.service';

@Component({
    selector: 'tt-dashboard-widget-base',
    templateUrl: './dashboard-widget-base.component.html',
    styleUrls: ['./dashboard-widget-base.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class DashboardWidgetBaseComponent extends BaseWidget {
    public override serialize(): NgCompInputs | undefined {
        return this.ttDashboardWidget ? { node: this.ttDashboardWidget } : undefined;
    }

    /**
     * Data to configure the dashboard widgets with.
     */
    @Input()
    public ttDashboardWidget?: WidgetTypes;

    /**
     * Event emitted when the dashboard widget has been changed.
     */
    @Output()
    public ttDashboardWidgetChange = new EventEmitter<WidgetTypes>();

    /**
     * Observable for when slicer data has changed. Updated when any data is changed, not only for this widgets data.
     */
    public slicersChanged?: BehaviorSubject<SlicerSubjectData[]>;

    /**
     * The last used slicer parameters used in the load data call relevant for this widget (not all slicer parameters active).
     */
    private lastSlicerParameters: SlicerSubjectData[] = [];

    /**
     * Whether the widget is currently loading data.
     */
    public loading = false;

    constructor(public elementRef: ElementRef, private utility: UtilityService, private state: StateService, private remember: RememberService, private datatask: DataTaskService, private dialog: MatDialog) {
        super();
    }

    /**
     * Handles click event for widgets.
     *
     * @param event the mouse event which triggered the click.
     */
    public onClick(event: MouseEvent) {
        if (!this.ttDashboardWidget) return;

        if (this.isChartWidget(this.ttDashboardWidget)) {
            this.openChartDialog();
        } else if (!this.isSlicerWidget(this.ttDashboardWidget)) {
            this.goto(event);
        }
    }

    /**
     * Opens the same chart in a dialog.
     */
    private openChartDialog() {
        this.dialog.open(DashboardChartModalComponent, { data: this.ttDashboardWidget, panelClass: 'tt-dashboard-chart-modal-panel', maxWidth: '100vw', maxHeight: '95vh', width: '120rem', height: '60rem' });
    }

    /**
     * Handles goto event for the widget using the given mouseclick event.
     *
     * @param event the mouseclick even twhich triggered the goto.
     */
    private async goto(event: MouseEvent) {
        if (this.isBasicWidget(this.ttDashboardWidget) || this.isGridWidget(this.ttDashboardWidget)) {
            this.loading = true;

            try {
                await Promise.all(Object.entries(this.ttDashboardWidget!.remember || {}).map(([key, value]) => this.remember.remember(key, value)));

                if (this.utility.isModifierKeyPressed(event)) {
                    this.state.newTab(this.ttDashboardWidget!.goto_item_state!, this.ttDashboardWidget.goto_item_parms);
                } else {
                    this.state.go(this.ttDashboardWidget!.goto_item_state!, this.ttDashboardWidget.goto_item_parms);
                }
            } finally {
                this.loading = false;
            }
        }
    }

    /**
     * Loads data to the widget.
     *
     * @param params additional parameters to include for the load data call if any.
     */
    private async loadData(params?: any) {
        this.loading = true;

        try {
            this.ttDashboardWidget = { ...this.ttDashboardWidget, ...(await this.datatask.Post(3518, { kpicomp_keyno: '' + this.ttDashboardWidget!.kpicomp_keyno, refresh: '0', ...this.getDashboardContextParams(), ...params }, { returnRawResponse: true })) };
            this.ttDashboardWidgetChange.emit(this.ttDashboardWidget);
        } finally {
            this.loading = false;
        }
    }

    private getDashboardContextParams(): Partial<DashboardContext> {
        return this.ttDashboardWidget?.dashboardContext ? { ...this.ttDashboardWidget.dashboardContext } : {};
    }

    /**
     * Reloads data for widget if the slicers set relevant for this component has changed since last used slicer parameters set.
     *
     * @param data the new slicer data set.
     */
    private onSlicersChanged = (data: SlicerSubjectData[]) => {
        const parameters = data.filter((d) => (this.ttDashboardWidget as DashboardWidgetKpi<any>)!.kpislicers.map((kpi) => kpi.kpislicer_keyno).includes(d.kpislicer_keyno));

        if (JSON.stringify(parameters) !== JSON.stringify(this.lastSlicerParameters)) {
            this.lastSlicerParameters = parameters;

            this.loadData({ slicerdata: parameters });
        }
    };

    async ngOnInit(): Promise<void> {
        this.slicersChanged = this.ttDashboardWidget?.slicersChanged();

        if (this.ttDashboardWidget && !this.isSlicerWidget(this.ttDashboardWidget) && this.ttDashboardWidget?.kpicomp_keyno) {
            this.loadData();

            this.ttDashboardWidget
                .forceRefreshSubject?.()
                ?.pipe(debounceTime(500))
                .subscribe({
                    next: async () => {
                        await this.loadData({ slicerdata: { ...this.slicersChanged?.value.filter((d) => (this.ttDashboardWidget as DashboardWidgetKpi<any>)!.kpislicers.map((kpi) => kpi.kpislicer_keyno).includes(d.kpislicer_keyno)) }, refresh: '1' });
                        this.ttDashboardWidget?.onLoaded?.();
                    },
                });

            if (this.ttDashboardWidget?.kpislicers && this.ttDashboardWidget.kpislicers.length > 0) {
                this.slicersChanged?.pipe(debounceTime(250)).subscribe({ next: this.onSlicersChanged });
            }
        }
    }

    /**
     * Checks whether the given widget configuration is a basic widget type (single text or numbers values only).
     *
     * @param value the widget configuration to check if is a basic widget type.
     * @returns `true` id the widget configuration is a basic widget type, `false` if not.
     */
    public isBasicWidget(value?: WidgetTypes): value is DashboardWidgetBasic {
        return !!value && Object.hasOwn(value, 'kpicomp_type') && (value as DashboardWidgetKpi<any>).kpicomp_type === 'BASIC';
    }

    /**
     * Checks whether the given widget configuration is a chart widget type.
     *
     * @param value the widget configuration to check if is a chart widget type.
     * @returns `true` id the widget configuration is a chart widget type, `false` if not.
     */
    public isChartWidget(value?: WidgetTypes): value is DashboardWidgetChart {
        if (value && Object.hasOwn(value, 'kpicomp_type')) {
            const val: DashboardWidgetKpi<any> = value as DashboardWidgetKpi<any>;
            return val.kpicomp_type === 'GRAPH_BAR' || val.kpicomp_type === 'GRAPH_BARH' || val.kpicomp_type === 'GRAPH_LINE' || val.kpicomp_type === 'PIE' || val.kpicomp_type === 'DONUT';
        }
        return false;
    }

    /**
     * Checks whether the given widget configuration is a slicer widget type.
     *
     * @param value the widget configuration to check if is a slicer widget type.
     * @returns `true` id the widget configuration is a slicer widget type, `false` if not.
     */
    public isSlicerWidget(value?: WidgetTypes): value is DashboardWidgetSlicer {
        return !!value && +value!.kpicomp_keyno! > 100000 && +value!.kpicomp_keyno! < 1000000;
    }

    public isGridWidget(value?: WidgetTypes): value is DashboardWidgetGrid {
        return !!value && 'kpicomp_type' in value && value.kpicomp_type === 'GRID';
    }
}

export interface WebpagemenuDashboardWidgetData {
    kpicomp_keyno: string | number;
    item_glyphicon?: string;
    item_glyphicon_color?: string;
    item_glyphicon_size?: string;
    tile_background_color?: string;
    tile_heading_text?: string;
    tile_heading_text_color?: string;
    tile_heading_text_size?: string;
    tile_subheading_text?: string;
    tile_subheading_text_color?: string;
    tile_subheading_text_size?: string;
    dashboardName?: string;
    dashboardContext?: DashboardContext;
    api: () => GridStack;
    slicersChanged: () => BehaviorSubject<SlicerSubjectData[]>;
    widgetClicked?: () => Subject<WidgetTypes>;
    forceRefreshSubject?: () => Subject<void>;
    onLoaded?: () => void;
}

export interface DashboardWidgetKpi<TType extends string> extends WebpagemenuDashboardWidgetData {
    /**
     * Parameters to update the portal user variable with, the key is the variablename and the value is the variablevalue.
     */
    remember?: { [key: string]: string };

    /**
     * The name of the kpi component.
     */
    kpicomp_name?: string;

    /**
     * The type of the kpi widget.
     */
    kpicomp_type: TType;

    /**
     * List of kpi splicers this kpi widget is dependent on.
     */
    kpislicers: KpiCompKpiSlicerData[];
}

/**
 * Represents the data object displayed in the charts.
 */
export interface GraphData {
    xkey: string;
    xorderby: number;
    ykey: string;
    yname: string;
    yvalue: number;
    x_state?: string;
    x_state_params?: string;
    x_state_mode?: DashboardChartStateMode;
    y_state?: string;
    y_state_params?: string;
    y_state_mode?: DashboardChartStateMode;
}

export type DashboardChartStateMode = 'default' | 'popup' | 'modal';

/**
 * Represents a dashboard widget in its most basic form, a single text value to be displayed.
 */
export interface DashboardWidgetBasic extends DashboardWidgetKpi<'BASIC'> {
    /**
     * The text to display in the widget.
     */
    data1_text?: string;

    /**
     * The state to goto when the widget is clicked.
     */
    goto_item_state?: string;

    /**
     * Parameters to use for the state route.
     */
    goto_item_parms?: string;
}

/**
 * Represents a dashboard widget in its most basic form, a single text value to be displayed.
 */
export interface DashboardWidgetGrid extends DashboardWidgetKpi<'GRID'> {
    /**
     * The state to goto when the widget is clicked.
     */
    goto_item_state?: string;

    /**
     * Parameters to use for the state route.
     */
    goto_item_parms?: string;

    p2_datatask_keyno?: number;
}

/**
 * Represents a dashboard widget of type chart.
 */
export interface DashboardWidgetChart extends DashboardWidgetKpi<'GRAPH_BAR' | 'GRAPH_BARH' | 'GRAPH_LINE' | 'PIE' | 'DONUT'> {
    /**
     * The dataset to display in the chart.
     */
    graphdata?: GraphData[];

    /**
     * The theme for the chart.
     */
    graphtheme?: string;
}

/**
 * Represents a dashboard widget of type slicer.
 */
export interface DashboardWidgetSlicer extends WebpagemenuDashboardWidgetData, KpiSlicerBaseData {}

/**
 * The possible different widget types to add to the dashboard.
 */
export type WidgetTypes = DashboardWidgetBasic | DashboardWidgetChart | DashboardWidgetSlicer | DashboardWidgetGrid;
